import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Tag, Briefcase, Plus, X, Lock, Info } from 'lucide-react';
import { courseCategoryService, industryService } from '../services/supabaseClient';
import '../styles/Settings.css';

const TONES = ['primary', 'blue', 'amber', 'green'];

export default function SettingsPage({ role }) {
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // DB CONNECTED: industries now come from primary_industries, same loading/error shape as categories
  const [industries, setIndustries] = useState([]);
  const [industriesLoading, setIndustriesLoading] = useState(true);

  const [newCategory, setNewCategory] = useState('');
  const [newIndustry, setNewIndustry] = useState('');

  const [categoryError, setCategoryError] = useState('');
  const [industryError, setIndustryError] = useState('');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const rows = await courseCategoryService.getAll();
        if (!cancelled) setCategories(rows);
      } catch (err) {
        if (!cancelled) setCategoryError('Could not load categories.');
      } finally {
        if (!cancelled) setCategoriesLoading(false);
      }
    })();

    // DB CONNECTED: fetch industries alongside categories
    (async () => {
      try {
        const rows = await industryService.getAll();
        if (!cancelled) setIndustries(rows);
      } catch (err) {
        if (!cancelled) setIndustryError('Could not load industries.');
      } finally {
        if (!cancelled) setIndustriesLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const addCategory = async (event) => {
    event.preventDefault();
    setCategoryError('');

    const name = newCategory.trim();
    if (!name) {
      setCategoryError('Enter a category name.');
      return;
    }

    const exists = categories.some(row => row.name.toLowerCase() === name.toLowerCase());
    if (exists) {
      setCategoryError('This category already exists.');
      return;
    }

    try {
      const created = await courseCategoryService.add(name);
      setCategories(prev => [...prev, created]);
      setNewCategory('');
    } catch (err) {
      setCategoryError(err.message || 'Could not add category.');
    }
  };

  const removeCategory = async (row) => {
    if (!window.confirm(`Remove category "${row.name}"?`)) return;

    const prevCategories = categories;
    setCategories(prev => prev.filter(item => item.category_id !== row.category_id));

    try {
      await courseCategoryService.remove(row.category_id);
    } catch (err) {
      setCategories(prevCategories);
      setCategoryError('Could not remove category — please retry.');
    }
  };

  /* ============================================================
     PARTNER INDUSTRIES — now backed by Supabase (primary_industries)
     mirrors the course-category add/remove pattern exactly
     ============================================================ */

  const addIndustry = async (event) => {
    event.preventDefault();
    setIndustryError('');

    const name = newIndustry.trim();
    if (!name) {
      setIndustryError('Enter an industry name.');
      return;
    }

    const exists = industries.some(row => row.name.toLowerCase() === name.toLowerCase());
    if (exists) {
      setIndustryError('This industry already exists.');
      return;
    }

    try {
      const created = await industryService.add(name);
      setIndustries(prev => [...prev, created]);
      setNewIndustry('');
    } catch (err) {
      setIndustryError(err.message || 'Could not add industry.');
    }
  };

  const removeIndustry = async (row) => {
    if (!window.confirm(`Remove industry "${row.name}"?`)) return;

    const prevIndustries = industries;
    setIndustries(prev => prev.filter(item => item.industry_id !== row.industry_id));

    try {
      await industryService.remove(row.industry_id);
    } catch (err) {
      setIndustries(prevIndustries);
      setIndustryError('Could not remove industry — please retry.');
    }
  };

  if (role === 'partner') {
    return (
      <div className="panel settings-locked">
        <span className="settings-locked-icon"><Lock size={17} /></span>
        <h2>Admins only</h2>
        <p>You don&apos;t have access to Settings. Contact an administrator if you need a change made here.</p>
      </div>
    );
  }

  return (
    <>
      <div className="hero settings-hero">
        <span className="settings-icon-badge"><SettingsIcon size={16} /></span>
        <div>
          <span className="eyebrow">Admin</span>
          <h2>Settings</h2>
          <p>Manage the course categories and partner industries used across WiiZ.</p>
        </div>
      </div>

      <div className="recent-grid settings-grid">
        <div className="panel settings-panel">
          <div className="panel-head">
            <div className="settings-panel-title">
              <span className="settings-panel-icon tone-primary"><Tag size={14} /></span>
              <div>
                <h3>Course Categories</h3>
                <p>Used when creating course or certification content.</p>
              </div>
            </div>
            <span className="chip">{categories.length}</span>
          </div>

          <form className="settings-add-row" onSubmit={addCategory}>
            <input
              type="text"
              placeholder="e.g. Cybersecurity"
              value={newCategory}
              onChange={event => setNewCategory(event.target.value)}
            />
            <button className="primary" type="submit">
              <Plus size={14} />
              Add
            </button>
          </form>

          {categoryError && <div className="error">{categoryError}</div>}

          <div className="settings-chip-list">
            {categoriesLoading && <div className="empty settings-empty">Loading categories…</div>}

            {!categoriesLoading && categories.map((row, index) => (
              <span key={row.category_id} className={`settings-chip tone-${TONES[index % TONES.length]}`}>
                {row.name}
                <button type="button" onClick={() => removeCategory(row)} aria-label={`Remove ${row.name}`}>
                  <X size={11} />
                </button>
              </span>
            ))}

            {!categoriesLoading && !categories.length && (
              <div className="empty settings-empty">No categories yet — add the first one above.</div>
            )}
          </div>

          <div className="notice settings-reference">
            <Info size={12} />
            <span>
              These show up in the <b>Category</b> field on the
              <b> Courses &amp; Certifications</b> page when adding new content.
            </span>
          </div>
        </div>

        {/* ---------------- PARTNER INDUSTRIES — now DB-connected ---------------- */}
        <div className="panel settings-panel">
          <div className="panel-head">
            <div className="settings-panel-title">
              <span className="settings-panel-icon tone-blue"><Briefcase size={14} /></span>
              <div>
                <h3>Partner Industries</h3>
                <p>Used when onboarding or filtering new partners.</p>
              </div>
            </div>
            <span className="chip">{industries.length}</span>
          </div>

          <form className="settings-add-row" onSubmit={addIndustry}>
            <input
              type="text"
              placeholder="e.g. Manufacturing"
              value={newIndustry}
              onChange={event => setNewIndustry(event.target.value)}
            />
            <button className="primary" type="submit">
              <Plus size={14} />
              Add
            </button>
          </form>

          {industryError && <div className="error">{industryError}</div>}

          <div className="settings-chip-list">
            {industriesLoading && <div className="empty settings-empty">Loading industries…</div>}

            {!industriesLoading && industries.map((row, index) => (
              <span key={row.industry_id} className={`settings-chip tone-${TONES[index % TONES.length]}`}>
                {row.name}
                <button type="button" onClick={() => removeIndustry(row)} aria-label={`Remove ${row.name}`}>
                  <X size={11} />
                </button>
              </span>
            ))}

            {!industriesLoading && !industries.length && (
              <div className="empty settings-empty">No industries yet — add the first one above.</div>
            )}
          </div>

          <div className="notice settings-reference">
            <Info size={12} />
            <span>
              These show up in the <b>Industry</b> field on the
              <b> Partners</b> page when adding a new partner.
            </span>
          </div>
        </div>
      </div>
    </>
  );
}